/**
 * @see {@link https://tailwindui.com/components/application-ui/feedback/alerts|Tailwind UI Alerts}
 */
import { cloneElement, type HTMLAttributes, type ReactElement } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import { tv } from 'tailwind-variants';

export type AlertSeverity = 'success' | 'warning' | 'error';

export type AlertProps = {
  title?: string;
  severity: AlertSeverity;
} & HTMLAttributes<HTMLDivElement>;

export function Alert({
  severity,
  children,
  title,
  className,
  ...props
}: AlertProps) {
  const style = alertStyle({ severity });
  return (
    <div className={style.container({ className })} {...props}>
      {cloneElement(iconMap[severity], {
        'aria-hidden': 'true',
        className: style.icon(),
      })}
      <div className={style.contentContainer()}>
        {title ? <h3 className={style.title()}>{title}</h3> : null}
        <div className={style.content()}>{children}</div>
      </div>
    </div>
  );
}

export const alertStyle = tv({
  slots: {
    container: 'flex rounded-md bg-yellow-50 p-4',
    icon: 'size-5 shrink-0',
    title: 'text-sm font-medium',
    contentContainer: 'ml-3 flex flex-col gap-2',
    content: 'text-sm',
  },
  variants: {
    severity: {
      warning: {
        container: 'bg-yellow-50',
        icon: 'text-yellow-400',
        title: 'text-yellow-800',
        content: 'text-yellow-700',
      },
      error: {
        container: 'bg-red-50',
        icon: 'text-red-400',
        title: 'text-red-800',
        content: 'text-red-700',
      },
      success: {
        container: 'bg-green-50',
        icon: 'text-green-400',
        title: 'text-green-800',
        content: 'text-green-700',
      },
    },
  },
});

const iconMap: Record<AlertProps['severity'], ReactElement> = {
  warning: <ExclamationTriangleIcon />,
  error: <XCircleIcon />,
  success: <CheckCircleIcon />,
};
